/**
 * chefri-cinematic.js
 * Premium cinematic experience layer for the Chef Ri Shopify theme.
 *
 * Sections handled:
 *   1. Page Loader          — Branded Chef Ri overlay on every page load
 *   2. Hero Entrance        — Scale zoom-in + logo float animation
 *                             (parallax is handled by hotline-split-hero.js — no duplication)
 *   3. Meet Ri              — Scroll-triggered image slide + text stagger + bg parallax
 *   4. Give In Showcase     — Sequential card opacity + image scale-in reveal
 *   5. Generic Reveals      — [data-cr-reveal] + auto-detected section headings
 *
 * Performance contract:
 *   ✓  Compositor-only animations: transform & opacity only — zero layout triggers
 *   ✓  IntersectionObserver for all scroll-based reveals (no scroll listener for reveals)
 *   ✓  requestAnimationFrame + ticking flag for any scroll-driven rAF loops
 *   ✓  passive: true on all addEventListener scroll calls
 *   ✓  will-change promoted before animation, cleared on transitionend
 *   ✓  Observers disconnected / unobserved immediately after firing
 *   ✓  prefers-reduced-motion: all motion fully skipped (elements shown immediately)
 *   ✓  No CLS — hidden elements use opacity (not display:none or height:0)
 *   ✓  No console errors — all DOM queries guarded with null checks
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════════
     CONSTANTS & UTILITIES
  ══════════════════════════════════════════════════════════ */

  /** True when the OS/browser has prefers-reduced-motion active */
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Double-rAF: ensures a style mutation is committed before the next measure.
   * Prevents the browser from skipping the transition by batching the set + paint.
   * @param {() => void} fn
   */
  function nextPaint(fn) {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  }

  /**
   * Run fn when DOM is ready. Handles both "already loaded" and "still parsing" cases.
   * @param {() => void} fn
   */
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /**
   * Cleanup will-change and inline transition after a transitionend event.
   * Restores CSS class-defined transitions so hover effects remain functional.
   * @param {HTMLElement} el
   */
  function cleanupAfterTransition(el) {
    el.addEventListener(
      'transitionend',
      () => {
        el.style.willChange = 'auto';
        el.style.transition = '';
      },
      { once: true }
    );
  }

  /* ══════════════════════════════════════════════════════════
     1. PAGE LOADER
     Branded full-screen overlay that fades out after window.load.
     The HTML is rendered in theme.liquid (to appear before JS runs).
     CSS keyframes are inlined in theme.liquid's <style> block.
  ══════════════════════════════════════════════════════════ */

  function initLoader() {
    const loader = document.getElementById('cr-page-loader');
    if (!loader) return;

    // Reduced motion: remove immediately so content is instantly accessible
    if (REDUCED) {
      loader.remove();
      return;
    }

    let dismissed = false;

    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      loader.classList.add('cr-loader--exit');
      // Remove from DOM entirely after fade completes — frees paint layer
      loader.addEventListener('transitionend', () => loader.remove(), { once: true });
      // Safety net if transitionend never fires (display:none, tab hidden, etc.)
      setTimeout(() => loader.remove(), 700);
    };

    // Hard cap: never hold the user more than 2.8 s
    const fallback = setTimeout(dismiss, 2800);

    const onLoad = () => {
      clearTimeout(fallback);
      // Small pause so hero entrance begins while the loader is still visible —
      // the scale-settle feels intentional when the loader clears
      setTimeout(dismiss, 260);
    };

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }
  }

  /* ══════════════════════════════════════════════════════════
     2. PAGE FADE-IN
     Gentle opacity entrance on every page arrival.
     Skipped when the theme's native View Transitions are enabled
     (settings.page_transition_enabled) to avoid double-animating.
  ══════════════════════════════════════════════════════════ */

  function initPageFadeIn() {
    const main = document.getElementById('MainContent');
    if (!main || REDUCED) return;

    // Respect Shopify theme's built-in cross-document View Transitions
    if (main.dataset.pageTransitionEnabled === 'true') return;

    main.style.opacity = '0';
    main.style.transition = 'opacity 0.5s ease';
    main.style.willChange = 'opacity';

    nextPaint(() => {
      main.style.opacity = '1';
      main.addEventListener(
        'transitionend',
        () => {
          main.style.willChange = 'auto';
          main.style.transition = '';
        },
        { once: true }
      );
    });
  }

  /* ══════════════════════════════════════════════════════════
     3. CINEMATIC HERO — Parallax + Scroll Fade
     Targets .cr-hero (the new single-image full-screen hero).
     a) Logo float: gentle bob on the sticky header logo img
     b) Scroll parallax: media layer drifts slowly upward
     c) Scroll fade: hero content + scroll indicator fade out
  ══════════════════════════════════════════════════════════ */

  function initHeroEntrance() {
    // ── a) Logo float on sticky header (all pages) ──
    const logoImg = document.querySelector('.chefri-logo-link .chefri-logo');
    if (logoImg && !REDUCED) {
      logoImg.classList.add('cr-logo-img--float');
    }

    // ── b+c) New hero parallax + content fade on scroll ──
    const hero = document.getElementById('cr-hero');
    if (!hero || REDUCED) return;

    const media   = document.getElementById('cr-hero-media');
    const content = document.getElementById('cr-hero-content');
    const scrollEl = document.getElementById('cr-hero-scroll');

    if (!media && !content) return;

    let heroTicking = false;

    const updateHeroParallax = () => {
      // ── CRITICAL: always reset ticking FIRST so no scroll event is ever dropped ──
      heroTicking = false;

      const scrollY = window.scrollY;
      const heroH   = hero.offsetHeight || window.innerHeight;

      // When hero is fully scrolled out — keep content hidden & bail early
      if (scrollY > heroH) {
        if (content) content.style.opacity = '0';
        return;
      }

      const progress = scrollY / heroH; // 0 → 1 as hero scrolls out

      // ── Parallax: image drifts up at 28% of scroll speed ──
      if (media) {
        media.style.transform = `translate3d(0, ${scrollY * 0.28}px, 0)`;
      }

      // ── Content: fade + rise as user scrolls, snap back when at top ──
      if (content) {
        if (scrollY <= 8) {
          // Guarantee text is fully visible when user returns to top
          content.style.opacity  = '1';
          content.style.transform = 'translate3d(0, 0, 0)';
        } else {
          const fadeStart  = 0.10;
          const fadeEnd    = 0.48;
          const opacity    = Math.max(0, 1 - (progress - fadeStart) / (fadeEnd - fadeStart));
          const translateY = scrollY * 0.16;
          content.style.opacity   = opacity.toFixed(3);
          content.style.transform = `translate3d(0, -${translateY}px, 0)`;
        }
      }

      // ── Scroll indicator fades quickly ──
      if (scrollEl) {
        const siOpacity = Math.max(0, 1 - progress / 0.18);
        scrollEl.style.opacity = siOpacity.toFixed(3);
      }
    };

    window.addEventListener('scroll', () => {
      if (heroTicking) return;
      heroTicking = true;
      requestAnimationFrame(updateHeroParallax);
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════════════════
     4. MEET RI — Scroll-Triggered Reveals
     - Image panel: slides in from the right
     - Content items: staggered fade + rise
     - Background squiggle: subtle vertical parallax
  ══════════════════════════════════════════════════════════ */

  function initMeetRi() {
    const section = document.querySelector('.meet-ri');
    if (!section) return;

    const image       = section.querySelector('.meet-ri__image');
    const bg          = section.querySelector('.meet-ri__bg');
    const eyebrow     = section.querySelector('.meet-ri__eyebrow');
    const heading     = section.querySelector('h2');
    const body        = section.querySelector('.meet-ri__body');
    const btn         = section.querySelector('.meet-ri__button');

    // Content items that stagger in after the image arrives
    const staggerItems = [eyebrow, heading, body, btn].filter(Boolean);

    /* ── Set initial hidden state (before intersection fires) ── */
    if (!REDUCED) {
      if (image) {
        image.style.opacity = '0';
        image.style.transform = 'translate3d(56px, 0, 0)';
        image.style.transition = [
          'opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
          'transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)',
        ].join(', ');
        image.style.willChange = 'transform, opacity';
      }

      staggerItems.forEach((el, i) => {
        // Each item starts invisible and slightly below its resting position
        el.style.opacity = '0';
        el.style.transform = 'translate3d(0, 22px, 0)';
        el.style.willChange = 'transform, opacity';
        // Stagger: image arrives first (0 ms base), then items cascade at 95 ms intervals
        const delay = i * 95 + 150;
        el.style.transition = [
          `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
          `transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        ].join(', ');
      });
    }

    /* ── Background squiggle parallax (scroll listener) ── */
    if (bg && !REDUCED) {
      let bgTicking = false;

      window.addEventListener(
        'scroll',
        () => {
          if (bgTicking) return;
          bgTicking = true;
          requestAnimationFrame(() => {
            const rect = section.getBoundingClientRect();
            const inView = rect.bottom > 0 && rect.top < window.innerHeight;
            if (inView) {
              // progress: 0 when section top is at viewport bottom, 1 when top is at viewport top
              const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
              // Shift bg squiggle subtly — clamp to prevent excessive travel
              const clampedProgress = Math.min(Math.max(progress, 0), 1);
              bg.style.transform = `translate3d(0, ${(clampedProgress - 0.5) * -48}px, 0)`;
            }
            bgTicking = false;
          });
        },
        { passive: true }
      );
    }

    /* ── IntersectionObserver — trigger reveals on scroll-into-view ── */
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (!REDUCED) {
            // Reveal image
            if (image) {
              image.style.opacity = '1';
              image.style.transform = 'translate3d(0, 0, 0)';
              image.addEventListener(
                'transitionend',
                () => {
                  image.style.willChange = 'auto';
                  // Keep inline transform at translate3d(0,0,0) to avoid reflow
                },
                { once: true }
              );
            }

            // Reveal content items (staggered via CSS transition-delay)
            staggerItems.forEach((el) => {
              el.style.opacity = '1';
              el.style.transform = 'translate3d(0, 0, 0)';
              cleanupAfterTransition(el);
            });
          }

          // Disconnect after first trigger — fire-once behavior
          revealObserver.disconnect();
        });
      },
      {
        // Trigger when 15% of the section is in view
        threshold: 0.15,
      }
    );

    revealObserver.observe(section);
  }

  /* ══════════════════════════════════════════════════════════
     5. GIVE IN SHOWCASE — Staggered Card Reveal
     - Cards: sequential opacity reveal (transform is NOT touched —
       the CSS layout uses translateY offsets on desktop)
     - Images inside cards: scale 1.07 → 1 for a subtle zoom-in
     - CTA button: fades in after all cards complete
  ══════════════════════════════════════════════════════════ */

  function initGiveIn() {
    const section = document.querySelector('.give-in-showcase');
    if (!section) return;

    const cards   = Array.from(section.querySelectorAll('.give-in-card'));
    const cta     = section.querySelector('.give-in-showcase__view-all');

    if (cards.length === 0) return;

    /* ── Set initial hidden state ── */
    if (!REDUCED) {
      cards.forEach((card, i) => {
        // Opacity only — we preserve CSS translateY layout offsets on desktop
        card.style.opacity = '0';
        card.style.transition = `opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${i * 80}ms`;
        card.style.willChange = 'opacity';

        // Image: start slightly zoomed in, land at natural size
        const img = card.querySelector('.give-in-card__image');
        if (img) {
          img.style.transform = 'scale3d(1.07, 1.07, 1)';
          // Offset image delay slightly so scale settles as card becomes visible
          img.style.transition = `transform 0.75s cubic-bezier(0.22, 1, 0.36, 1) ${i * 80 + 40}ms`;
          img.style.willChange = 'transform';
        }
      });

      if (cta) {
        // CTA enters after the last card's stagger completes
        const ctaDelay = cards.length * 80 + 160;
        cta.style.opacity = '0';
        cta.style.transform = 'translate3d(0, 18px, 0)';
        cta.style.willChange = 'transform, opacity';
        cta.style.transition = [
          `opacity 0.55s ease ${ctaDelay}ms`,
          `transform 0.55s ease ${ctaDelay}ms`,
        ].join(', ');
      }
    }

    /* ── IntersectionObserver — trigger when showcase scrolls into view ── */
    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (!REDUCED) {
            cards.forEach((card) => {
              card.style.opacity = '1';
              // Clean up the reveal transition so existing CSS hover transitions work
              card.addEventListener(
                'transitionend',
                () => {
                  card.style.willChange = 'auto';
                  card.style.transition = '';
                },
                { once: true }
              );

              const img = card.querySelector('.give-in-card__image');
              if (img) {
                img.style.transform = 'scale3d(1, 1, 1)';
                img.addEventListener(
                  'transitionend',
                  () => {
                    // Clear inline transition to restore the CSS-defined hover scale
                    img.style.transition = '';
                    img.style.willChange = 'auto';
                  },
                  { once: true }
                );
              }
            });

            if (cta) {
              cta.style.opacity = '1';
              cta.style.transform = 'translate3d(0, 0, 0)';
              cleanupAfterTransition(cta);
            }
          }

          cardObserver.disconnect();
        });
      },
      {
        threshold: 0.08,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    cardObserver.observe(section);
  }

  /* ══════════════════════════════════════════════════════════
     5b. FEATURED DESSERTS — Staggered Card Entrance
     Targets .cr-fd-card elements in #cr-featured-desserts.
     Cards scale 0.97 → 1, opacity 0 → 1 with 80ms stagger.
     Uses the CSS class .cr-fd-card--visible toggled by IO.
  ══════════════════════════════════════════════════════════ */

  function initFeaturedDesserts() {
    const section = document.getElementById('cr-featured-desserts');
    if (!section) return;

    const cards = Array.from(section.querySelectorAll('[data-cr-fd-card]'));
    if (cards.length === 0) return;

    if (REDUCED) {
      // Skip animation — show everything immediately
      cards.forEach((c) => c.classList.add('cr-fd-card--visible'));
      return;
    }

    const fdObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          cards.forEach((card, i) => {
            setTimeout(() => {
              card.classList.add('cr-fd-card--visible');
            }, i * 90);
          });

          fdObserver.disconnect();
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    fdObserver.observe(section);
  }

  /* ══════════════════════════════════════════════════════════
     6. GENERIC SECTION REVEALS
     Two modes:
       a) Manual: any element with [data-cr-reveal] gets a reveal
          (supports [data-cr-delay="400"] for stagger control)
       b) Auto: h2/h3 headings inside section-wrappers not already
          handled by initMeetRi / initGiveIn are auto-detected
  ══════════════════════════════════════════════════════════ */

  function initGenericReveals() {
    if (REDUCED) return;

    // a) Manual targets
    const manual = Array.from(document.querySelectorAll('[data-cr-reveal]'));

    // b) Auto-detect headings in other sections (exclude already-handled ones)
    const autoHeadings = Array.from(
      document.querySelectorAll('.section-wrapper h2, .section-wrapper h3')
    ).filter((el) => {
      return (
        !el.closest('.meet-ri') &&
        !el.closest('.give-in-showcase') &&
        !el.closest('.hero-wrapper') &&
        // Exclude elements that are already in the manual list
        !el.hasAttribute('data-cr-reveal')
      );
    });

    const targets = [...manual, ...autoHeadings];
    if (targets.length === 0) return;

    /* ── Set initial hidden state ── */
    targets.forEach((el) => {
      const delay = Number(el.dataset.crDelay) || 0;
      el.style.opacity = '0';
      el.style.transform = 'translate3d(0, 25px, 0)';
      el.style.willChange = 'transform, opacity';
      el.style.transition = [
        `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        `transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      ].join(', ');
    });

    /* ── IntersectionObserver — each element observed individually ── */
    const genericObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = /** @type {HTMLElement} */ (entry.target);
          el.style.opacity = '1';
          el.style.transform = 'translate3d(0, 0, 0)';
          // unobserve immediately — each fires once only
          genericObserver.unobserve(el);
          cleanupAfterTransition(el);
        });
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -20px 0px',
      }
    );

    targets.forEach((el) => genericObserver.observe(el));
  }

  /* ══════════════════════════════════════════════════════════
     7. DESKTOP HEADER SCROLL INTERACTION
     Handles desktop-only logo scale & position animations
     as well as header transition state switches on ALL pages.
  ══════════════════════════════════════════════════════════ */

  function initHeaderScroll() {
    const header = document.querySelector('.chefri-custom-header');
    if (!header || REDUCED) return;

    const isDesktop = window.matchMedia('(min-width: 769px)');
    const wrapper = document.querySelector('.page-wrapper');

    function updateHeader() {
      if (!isDesktop.matches) {
        // Reset classes on mobile/tablet viewports
        header.classList.remove('chefri-header--top');
        header.classList.remove('chefri-custom-header--scrolled');
        return;
      }

      const scrollTop = wrapper ? wrapper.scrollTop : window.scrollY;
      const scrolled = scrollTop > 58;
      if (scrolled) {
        header.classList.remove('chefri-header--top');
        header.classList.add('chefri-custom-header--scrolled');
      } else {
        header.classList.add('chefri-header--top');
        header.classList.remove('chefri-custom-header--scrolled');
      }
    }

    // Set initial classes
    updateHeader();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateHeader();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    if (wrapper) {
      wrapper.addEventListener('scroll', onScroll, { passive: true });
    }

    // Handle resize/viewport state shifts
    isDesktop.addEventListener('change', updateHeader);
  }

  /* ══════════════════════════════════════════════════════════
     8. FOOTER REVEAL
     Subtle scroll-triggered fade in of the global footer.
  ══════════════════════════════════════════════════════════ */

  function initFooterReveal() {
    const footer = document.querySelector('footer');
    if (!footer || REDUCED) return;

    footer.style.opacity = '0';
    footer.style.transform = 'translate3d(0, 25px, 0)';
    footer.style.willChange = 'transform, opacity';
    footer.style.transition = [
      'opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
      'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)'
    ].join(', ');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        footer.style.opacity = '1';
        footer.style.transform = 'translate3d(0, 0, 0)';
        observer.unobserve(footer);
        cleanupAfterTransition(footer);
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -10px 0px'
    });

    observer.observe(footer);
  }

  /* ══════════════════════════════════════════════════════════
     BOOT
  ══════════════════════════════════════════════════════════ */

  onReady(() => {
    initLoader();
    initPageFadeIn();
    initHeroEntrance();
    initFeaturedDesserts();
    initMeetRi();
    initGiveIn();
    initGenericReveals();
    initHeaderScroll();
    initFooterReveal();
  });
})();
